import { EntradasMapper } from '@/domain/simulador/entradas-mapper'
import type { TSimulacaoCalculoRotinaArquivo } from '@/domain/simulador/factories/types/simulacao-calculo'
import { IntermediariasMapper } from '@/domain/simulador/intermediarias-mapper'
import { MensaisMapper } from '@/domain/simulador/mensais-mapper'
import { CalculadoraDeFatorDeVendedor } from '@/features/simulador/brasil/domain/calculadora-de-fator-vendedor'
import { CalculadoraDeParcelasSacocVParcelado } from '@/features/simulador/brasil/domain/calculadora-de-parcelas-sacocv-parcelado'
import { CalculadoraDeValorPresenteSacocVParcelado } from '@/features/simulador/brasil/domain/calculadora-de-valor-presente-sacocv-parcelado'
import { Arquivo } from '@/features/simulador/shared/domain/arquivo'
import { CalculadoraDeComissao } from '@/features/simulador/shared/domain/calculadora-de-comissao'
import { CalculadoraDeFator } from '@/features/simulador/shared/domain/calculadora-de-fator'
import { CalculadoraDeMargem } from '@/features/simulador/shared/domain/calculadora-de-margem'
import { CalculadoraDeValorMedio } from '@/features/simulador/shared/domain/calculadora-de-valor-medio'
import { ConversorJuros } from '@/features/simulador/shared/domain/conversor-juros'
import { Mascara } from '@/features/simulador/shared/domain/mascara'
import { Rotina } from '@/features/simulador/shared/domain/rotina'
import type { IParcela } from '@/features/simulador/shared/types/calculadora-parcela.types'
import { CalculadoraDeValoresTotaisSacocVParcelado } from '@/utils/calculadora-valores-totais-sacocv-parcelado'
import { resolvePathWithQuery } from '@/utils/object.utils'

import type { TParametrosRotinaArquivo } from './rotina-arquivo-base'

export class RotinaArquivoSacocVParcelado {
  private readonly calculadoraComissao: CalculadoraDeComissao

  constructor(
    readonly parametros: TParametrosRotinaArquivo,
    private readonly calculadoraValorPresente: CalculadoraDeValorPresenteSacocVParcelado = new CalculadoraDeValorPresenteSacocVParcelado(),
    private readonly calculadoraParcelas: CalculadoraDeParcelasSacocVParcelado = new CalculadoraDeParcelasSacocVParcelado(),
    private readonly calculadoraDeMargem: CalculadoraDeMargem = new CalculadoraDeMargem(),
    private readonly calculadoraDeFator: CalculadoraDeFator = new CalculadoraDeFator(),
    private readonly mascara: Mascara = new Mascara(),
  ) {
    this.calculadoraComissao = new CalculadoraDeComissao(this.parametros.percentualComissaoVendedor)
  }

  public calcular(grupos: TSimulacaoCalculoRotinaArquivo) {
    const dataSimulacao = grupos.dataSimulacao
    const entradas = EntradasMapper.transform(grupos.entradas ?? [])
    const mensais = MensaisMapper.transform(grupos.mensais ?? [])
    const intermediarias = IntermediariasMapper.transform(grupos.intermediarias ?? [])
    const posConclusao = MensaisMapper.transform(grupos.posConclusao ? [grupos.posConclusao] : [])
    const intermediariasPosConclusao = IntermediariasMapper.transform(grupos.intermediariasPosConclusao ?? [])

    const gruposParcelas = { entradas, mensais, intermediarias, posConclusao, intermediariasPosConclusao }

    const tiposParcelas = Object.keys(gruposParcelas) as Array<keyof typeof gruposParcelas>
    let parcelas: IParcela[] = tiposParcelas.flatMap((tipo) =>
      gruposParcelas[tipo].flatMap((grupo) => grupo.parcelas.map((parcela) => ({ ...parcela, tipo }))),
    )

    const taxaJurosDiarioAntesPontoMedio = ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
      this.parametros.percentualJurosAnualAntesPontoMedio,
    )
    const taxaJurosDiarioDepoisPontoMedio = ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
      this.parametros.percentualJurosAnualDepoisPontoMedio,
    )

    parcelas = this.calculadoraValorPresente.calcularValorPresenteDasParcelas(
      parcelas,
      dataSimulacao,
      taxaJurosDiarioDepoisPontoMedio,
      this.parametros.dataSaldo,
    )

    const valorPresente = this.calculadoraParcelas.somar(parcelas, 'valorPresente')

    const comissaoVendedor = this.calculadoraComissao.calcularComissaoVendedor(valorPresente)

    parcelas = this.calculadoraParcelas.calcularValorDeImpostoDasParcelas(
      parcelas,
      this.parametros.percentualImpostos,
      this.parametros.percentualISS,
      'valor',
    )

    parcelas = this.calculadoraParcelas.calcularValorSemImpostoEComissaoDasParcelas(parcelas, comissaoVendedor, 'valor')

    parcelas = this.calculadoraParcelas.calcularValorDePontoMedioDasParcelas(
      parcelas,
      taxaJurosDiarioAntesPontoMedio,
      taxaJurosDiarioDepoisPontoMedio,
      this.parametros.dataPontoMedio,
      this.parametros.dataSaldo,
    )

    const valorMedioTotal = this.calculadoraParcelas.somar(parcelas, 'valorPontoMedio')

    const valorMedioTotalUPF = CalculadoraDeValorMedio.calcularEmUPF(valorMedioTotal, this.parametros.taxaUpf)

    const margemBrutaEmUPF = this.calculadoraDeMargem.calcularMargemBrutaEmUPF(
      valorMedioTotalUPF,
      this.parametros.custoEmUPF,
    )

    const margemLiquidaEmUPF = this.calculadoraDeMargem.calcularMargemLiquidaEmUPF(
      margemBrutaEmUPF,
      this.parametros.patrimonioAfetacao,
      this.parametros.modoDeLucroDaEmpresa,
      this.parametros.percentualIRPJ,
    )

    const margemRealizada = this.calculadoraDeMargem.calcularMargemRealizada(margemLiquidaEmUPF, {
      margemGR: this.parametros.margemGR,
      margemGcom: this.parametros.margemGcom,
      margemVendedor1Por3: this.parametros.margemVendedor1Por3,
      margemVendedor2Por3: this.parametros.margemVendedor2Por3,
      margemVendedor3Por3: this.parametros.margemVendedor3Por3,
    })

    const margens = this.calculadoraDeFator.calcularFatorAlcancado(margemRealizada)

    const calculadoraDeValoresTotais = new CalculadoraDeValoresTotaisSacocVParcelado(grupos)
    const { valorPosConclusao: totalSaldo, valorPoupanca } = calculadoraDeValoresTotais

    const porcentagemPoupanca = (valorPoupanca * 100) / (valorPoupanca + totalSaldo)
    const fatorVendedor = CalculadoraDeFatorDeVendedor.calcular(margens, porcentagemPoupanca, this.parametros) ?? 0

    const fatorVendedor1Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor1Por3].fator') ?? 0
    const fatorVendedor2Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor2Por3].fator') ?? 0
    const fatorVendedor3Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor3Por3].fator') ?? 0
    const fatorGcom = resolvePathWithQuery(margens, '[tipo=margemGcom].fator') ?? 0
    const fatorGR = resolvePathWithQuery(margens, '[tipo=margemGR].fator') ?? 0

    const { mascaraGcom, mascaraGR, mascaraVendedor } = this.mascara.aplicarMascaraNosFatores(
      { fatorVendedor, fatorGcom, fatorGR },
      this.parametros,
    )

    const valorPresentePontoMedio = this.calculadoraValorPresente.calcularValorPresenteNoPontoMedio({
      valorPresente,
      taxaJurosDiarioDepoisPontoMedio,
      dataSimulacao,
      dataPontoMedio: this.parametros.dataPontoMedio,
    })

    const valorPresentePontoMedioUPF = this.calculadoraValorPresente.calcularValorPresenteNoPontoMedioEmUPF(
      valorPresentePontoMedio,
      this.parametros.taxaUpf,
    )

    const percentualMargemVenda = this.calculadoraDeMargem.calcularPorcentagemMargemDeVenda(
      margemLiquidaEmUPF,
      valorPresentePontoMedioUPF,
    )

    const rotina = Rotina.montar({
      ordemMascaraVendedor: this.parametros.ordemMascaraVendedor,
      mascaraVendedor,
      percentualMargemVenda,
    })

    const arquivo = Arquivo.montar(this.mascara, {
      valorPresente,
      ordemMascaraGcom: this.parametros.ordemMascaraGcom,
      ordemMascaraGR: this.parametros.ordemMascaraGR,
      mascaraGcom,
      mascaraGR,
    })

    return {
      valorPresente: Number(valorPresente.toFixed(2)),
      comissaoVendedor: Number(comissaoVendedor.toFixed(2)),
      valorMedioTotal: Number(valorMedioTotal.toFixed(2)),
      valorMedioTotalUPF: Number(valorMedioTotalUPF.toFixed(2)),
      margemBruta: Number(margemBrutaEmUPF.toFixed(2)),
      margemLiquida: Number(margemLiquidaEmUPF.toFixed(2)),
      fatorVendedor1Por3: Number(fatorVendedor1Por3.toFixed(3)),
      fatorVendedor2Por3: Number(fatorVendedor2Por3.toFixed(3)),
      fatorVendedor3Por3: Number(fatorVendedor3Por3.toFixed(3)),
      fatorGcom: Number(fatorGcom.toFixed(3)),
      fatorGR: Number(fatorGR.toFixed(3)),
      valorPresentePontoMedio: Number(valorPresentePontoMedio.toFixed(2)),
      valorPresentePontoMedioUPF: Number(valorPresentePontoMedioUPF.toFixed(2)),
      percentualMargemVenda: Number(percentualMargemVenda.toFixed(2)),
      rotina,
      arquivo,
    }
  }
}
